import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Download, Infinity, Smartphone, Users, Tag, Star } from 'lucide-react';
import { PaymentLogos } from './PaymentLogos';
import { useCart } from '../context/CartContext';
const trustPoints = [{
  icon: ShieldCheck,
  label: 'Pago protegido',
  detail: 'mediante Mercado Pago.'
}, {
  icon: Download,
  label: 'Descarga inmediata',
  detail: 'apenas se acredita el pago.'
}, {
  icon: Infinity,
  label: 'Acceso de por vida',
  detail: 'al recetario para descargarlo cuando quieras.'
}, {
  icon: Smartphone,
  label: 'Compatible',
  detail: 'con cualquier celular, PC o tablet.'
}, {
  icon: Users,
  label: '+4.600 personas',
  detail: 'ya compraron este recetario y lo están usando.'
}];
interface PricingSectionProps {
  id?: string;
  showPaymentLogos?: boolean;
}
export function PricingSection({
  id,
  showPaymentLogos = true
}: PricingSectionProps) {
  const {
    setCartOpen
  } = useCart();
  const [viewerCount, setViewerCount] = useState(() => Math.floor(Math.random() * (120 - 80 + 1)) + 80);
  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(prev => {
        const change = Math.floor(Math.random() * 7) - 3; // -3 to +3
        const next = prev + change;
        return Math.max(80, Math.min(120, next));
      });
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, []);
  return <section id={id} className="w-full py-16 md:py-24 px-6 bg-background" data-id="element-328">
      <div className="max-w-[1280px] mx-auto" data-id="element-329">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-8 md:gap-14 items-start" data-id="element-330">
          {/* Product Image */}
          <div className="w-full" data-id="element-331">
            <img src="https://cdn.magicpatterns.com/uploads/2a1kXd3MrJbqR8Ugb5Uuvq/recetas-air-fryer-argentinas-mockup3d.png" alt="Recetas Air Fryer Argentinas — portada del eBook" className="w-full h-auto object-contain rounded-2xl" data-id="element-332" />
          </div>

          {/* Product Details */}
          <div className="flex flex-col" data-id="element-333">
            {/* Social proof small */}
            <p className="text-xs text-charcoal/50 mb-2 flex items-center gap-1" data-id="element-334">
              ⭐ +4.600 descargas verificadas
            </p>

            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-[42px] font-serif leading-tight text-charcoal mb-4" data-id="element-335">
              EBOOK: RECETAS AIR FRYER ARGENTINAS
            </h2>
            <p className="text-lg text-charcoal/70 mb-4" data-id="element-336">
              100 recetas argentinas{' '}
              <img src="https://flagcdn.com/w40/ar.png" alt="Argentina" className="inline-block w-5 h-auto align-middle ml-1" data-id="element-337" />
            </p>

            {/* Star rating */}
            <div className="flex items-center gap-2 mb-5" data-id="element-338">
              <div className="flex text-yellow-400" data-id="element-339">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-340" />)}
              </div>
              <span className="text-sm text-charcoal/70" data-id="element-341">
                4.9/5 (312 Reseñas)
              </span>
            </div>

            {/* Pricing Row */}
            <div className="flex items-baseline flex-wrap gap-3 mb-5" data-id="element-342">
              <span className="text-xl text-charcoal/40 line-through" data-id="element-343">
                $29.980,00
              </span>
              <span className="text-3xl md:text-4xl font-bold text-primary" data-id="element-344">
                $14.990,00
              </span>
              <span className="inline-flex items-center gap-1 bg-primary text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" data-id="element-345">
                <Tag className="w-3 h-3" data-id="element-346" />
                AHORRÁS 50%
              </span>
            </div>

            {/* Urgency Banner */}
            <div className="bg-surface border border-primary/15 rounded-xl px-4 py-3 mb-6 flex items-center gap-2 text-sm font-medium text-charcoal" data-id="element-347">
              <span className="text-lg" data-id="element-348">🎁</span>
              <span data-id="element-349">
                <strong className="text-primary" data-id="element-350">50% OFF</strong> — Últimos{' '}
                <strong data-id="element-351">8 cupos</strong> disponibles
              </span>
            </div>

            {/* CTA Button — Direct checkout */}
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            window.location.href = 'https://valordigital.store/cart/46518849142973:1';
          }} className="w-full bg-primary hover:bg-primary-hover text-white font-bold uppercase tracking-wider py-4 px-8 rounded-xl shadow-soft transition-colors text-lg mb-2" data-id="element-352">
              Descargar ahora
            </motion.button>

            {/* Secondary CTA — Cart with upsells */}
            <button onClick={() => setCartOpen(true)} className="w-full text-primary font-medium text-sm py-2 mb-4 hover:underline transition-colors" data-id="element-353">
              Ver opciones adicionales →
            </button>

            {/* Live viewers */}
            <p className="text-sm text-charcoal/60 mb-8 flex items-center gap-2" data-id="element-354">
              <span className="relative flex h-2.5 w-2.5" data-id="element-355">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" data-id="element-356"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" data-id="element-357"></span>
              </span>
              <span data-id="element-358">
                <strong className="text-charcoal/80" data-id="element-359">
                  {viewerCount} personas
                </strong>{' '}
                están viendo este producto ahora mismo.
              </span>
            </p>

            {/* Trust Box */}
            <div className="bg-surface border border-charcoal/5 rounded-2xl p-6" data-id="element-360">
              <div className="flex items-center gap-2 mb-4" data-id="element-361">
                <span className="text-lg" data-id="element-362">🔒</span>
                <h3 className="font-bold text-charcoal text-base" data-id="element-363">
                  Compra rápida, segura y sin complicaciones
                </h3>
              </div>

              <ul className="space-y-3" data-id="element-364">
                {trustPoints.map((point, index) => <li key={index} className="flex items-start gap-3 text-sm text-charcoal/80 leading-relaxed" data-id="element-365">
                    <span className="mt-0.5 text-charcoal/30" data-id="element-366">•</span>
                    <span data-id="element-367">
                      <strong className="text-charcoal" data-id="element-368">{point.label}</strong>{' '}
                      {point.detail}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Payment Methods */}
            {showPaymentLogos && <div className="mt-6" data-id="element-369">
                <p className="text-xs text-charcoal/50 mb-2 font-medium text-center md:text-left" data-id="element-370">
                  Medios de pago aceptados
                </p>
                <PaymentLogos data-id="element-371" />
              </div>}
          </div>
        </motion.div>
      </div>
    </section>;
}